import { Injectable } from '@angular/core';
import { ToastService } from '@g99-product-web-ui/shared-service';

import { UploadedFile } from './multi-file-upload.component';

@Injectable({
  providedIn: 'root',
})
export class MultiFileUploadService {
  /**
   * Validates the size of a single file to ensure it does not exceed the given maximum allowed size.
   *
   * @param file The file to validate.
   * @param maxFileSizeMB The maximum allowed file size in megabytes.
   * @returns {boolean} True if the file size is valid, false otherwise.
   */

  constructor(private readonly _toastService: ToastService) {}
  public validateFileSize(file: File, maxFileSizeMB: number): boolean {
    // Calculate the maximum allowed file size in bytes
    const maxFileSizeBytes = maxFileSizeMB * 1024 * 1024;

    // Check if the file size is less than or equal to the maximum allowed size
    return file.size <= maxFileSizeBytes;
  }

  /**
   * Validates and filters the given array of files to only include those that
   * do not exceed the given maximum allowed file size.
   *
   * @param files The array of files to validate and filter.
   * @param maxFileSizeMB The maximum allowed file size in megabytes.
   * @returns {File[]} The filtered array of files, with only the files that
   * are within the allowed size.
   */
  public validateAndFilterFilesByMaxSize(
    files: File[],
    maxFileSizeMB: number
  ): File[] {
    // Calculate the maximum allowed file size in bytes
    const maxFileSizeBytes = maxFileSizeMB * 1024 * 1024;
    // Filter the files to only include those that are within the allowed size
    return files.filter((file) => file.size <= maxFileSizeBytes);
  }
  /**
   * Validates the number of files to ensure it does not exceed the given maximum allowed number.
   *
   * @param files The array of files to validate.
   * @param maxFiles The maximum allowed number of files.
   * @returns {boolean} True if the number of files is valid, false otherwise.
   */

  public validateMaxNumberOfFiles(files: File[], maxFiles: number): boolean {
    return files.length > maxFiles;
  }

  public simulateUploadProgress(fileData: UploadedFile): void {
    // Simulate upload progress for the file
    const interval = setInterval(() => {
      if (fileData.progress < 100) {
        fileData.progress += 10; // Increment progress by 10%
      } else {
        clearInterval(interval); // Stop the interval when progress reaches 100%
      }
    }, 500); // Update every 500 milliseconds
  }

  public createFilePreview(file: File): string {
    const reader = new FileReader();
    let previewUrl = '';

    reader.onload = () => {
      previewUrl = reader.result as string;
    };
    reader.readAsDataURL(file);

    return previewUrl;
  }

  /**
   * Formats file size from bytes to human readable format.
   *
   * @param bytes The file size in bytes.
   * @returns {string} The formatted file size.
   */
  public formatFileSize(bytes: number): string {
    if (bytes === 0) {
      return '0 B';
    }
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  }

  // ==================== FILE TYPE VALIDATION METHODS ====================

  /**
   * Filter files by allowed types
   * Supports both MIME types (image/jpeg) and extensions (JPG, PNG)
   */
  public filterFilesByType(
    files: File[],
    allowedTypes: string[]
  ): {
    validFiles: File[];
    rejectedFiles: File[];
  } {
    console.log(files, allowedTypes);
    // If no restrictions, allow all files
    if (!allowedTypes || allowedTypes.length === 0) {
      return { validFiles: files, rejectedFiles: [] };
    }

    const validFiles: File[] = [];
    const rejectedFiles: File[] = [];

    files.forEach((file) => {
      if (this.isFileTypeAllowed(file, allowedTypes)) {
        validFiles.push(file);
      } else {
        rejectedFiles.push(file);
      }
    });

    return { validFiles, rejectedFiles };
  }

  /**
   * Check if a file type is allowed
   * Supports both MIME types (image/jpeg) and extensions (JPG, PNG)
   */
  public isFileTypeAllowed(file: File, allowedTypes: string[]): boolean {
    // If no restrictions, allow all files
    if (!allowedTypes || allowedTypes.length === 0) {
      return true;
    }

    const fileName = file.name.toLowerCase();
    const fileExtension = fileName.split('.').pop() || '';
    const fileMimeType = file.type.toLowerCase();

    return allowedTypes.some((allowedType) => {
      const normalizedType = allowedType.toLowerCase();

      // Check for MIME type patterns (e.g., "image/jpeg", "image/*")
      if (normalizedType.includes('/')) {
        // Handle wildcard patterns like "image/*"
        if (normalizedType.endsWith('/*')) {
          const category = normalizedType.split('/')[0];
          return fileMimeType.startsWith(`${category}/`);
        }
        // Exact MIME type match
        return fileMimeType === normalizedType;
      }

      // Check for extension match (e.g., "jpg", "png", ".PDF")
      const normalizedExt = normalizedType.startsWith('.')
        ? normalizedType.slice(1)
        : normalizedType;
      return fileExtension === normalizedExt;
    });
  }

  /**
   * Get user-friendly display of allowed file types
   */
  public getAllowedTypesDisplay(allowedTypes: string[]): string {
    if (!allowedTypes || allowedTypes.length === 0) {
      return 'all';
    }

    // Check if all are MIME types starting with "image/"
    const allImages = allowedTypes.every((type) =>
      type.toLowerCase().startsWith('image/')
    );

    if (allImages) {
      return 'image';
    }

    // Check if all are MIME types starting with "video/"
    const allVideos = allowedTypes.every((type) =>
      type.toLowerCase().startsWith('video/')
    );

    if (allVideos) {
      return 'video';
    }

    // Otherwise, display the types/extensions
    const displayTypes = allowedTypes.map((type) => {
      if (type.includes('/')) {
        // For MIME types, extract the subtype
        const parts = type.split('/');
        return parts[1]?.replace('*', 'all') || parts[0];
      }
      return type;
    });

    return displayTypes.join(', ').toUpperCase();
  }

  /**
   * Generate proper accept attribute for file input
   */
  public getAcceptAttribute(allowedTypes: string[]): string {
    if (!allowedTypes || allowedTypes.length === 0) {
      return '*';
    }

    // Convert to proper format for accept attribute
    return allowedTypes
      .map((type) => {
        // If already a MIME type, use as is
        if (type.includes('/')) {
          return type;
        }
        // If extension, prefix with dot
        return `.${type.toLowerCase()}`;
      })
      .join(',');
  }

  public processInstagramImage(file: File): Promise<{
    file: File;
    imageRatio: '1:1' | '4:5';
  }> {
    return new Promise((resolve, reject) => {
      const image = new Image();

      image.onload = () => {
        const width = image.naturalWidth;
        const height = image.naturalHeight;

        const ratio = width / height;
        const isFourByFive = Math.abs(ratio - 4 / 5) < 0.01;

        const imageRatio = isFourByFive ? '4:5' : '1:1';

        let targetWidth = 600;
        let targetHeight = 600;

        if (isFourByFive) {
          // Preserve 4:5
          targetWidth = 480;
          targetHeight = 600;
        }

        const canvas = document.createElement('canvas');

        canvas.width = targetWidth;
        canvas.height = targetHeight;

        const context = canvas.getContext('2d');

        if (!context) {
          URL.revokeObjectURL(image.src);
          reject(new Error('Unable to create canvas context'));
          return;
        }

        const scale = Math.max(targetWidth / width, targetHeight / height);
        const scaledWidth = width * scale;
        const scaledHeight = height * scale;

        const offsetX = (scaledWidth - targetWidth) / 2;
        const offsetY = (scaledHeight - targetHeight) / 2;

        context.drawImage(image, -offsetX, -offsetY, scaledWidth, scaledHeight);

        canvas.toBlob(
          (blob) => {
            URL.revokeObjectURL(image.src);

            if (!blob) {
              reject(new Error('Unable to process image'));
              return;
            }

            const processedFile = new File([blob], file.name, {
              type: file.type,
              lastModified: Date.now(),
            });

            resolve({
              file: processedFile,
              imageRatio,
            });
          },
          file.type,
          0.9
        );
      };

      image.onerror = () => {
        URL.revokeObjectURL(image.src);
        reject(new Error('Unable to read image'));
      };

      image.src = URL.createObjectURL(file);
    });
  }
}
